import React, { useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import { FormControl, Grid, Chip, Box } from '@mui/material';

export default function SelectRebuild({
  options,
  label,
  multiple = false,
  loading = false,
  register,
  setValue,
  watch,
  name,
  allOption = { id: 'ALL', name: 'All' },
  enableAllOption = false,
}) {
  const currentValue = watch(name);
  const [isOpen, setIsOpen] = useState(false);

  const optionsWithAll = useMemo(() => {
    if (multiple || enableAllOption) {
      return [allOption, ...options];
    }
    return options;
  }, [options, multiple, allOption, enableAllOption]);

  const handleChange = (_, newValue) => {
    if (multiple) {
      const isAllSelected = newValue.some((v) => String(v.id).toUpperCase() === 'ALL');
      const wasAllSelected = currentValue?.length === options.length;

      if (isAllSelected && !wasAllSelected) {
        setValue(name, options);
      } else if (!isAllSelected && wasAllSelected) {
        setValue(name, []);
      } else {
        setValue(
          name,
          newValue.filter((v) => String(v.id).toUpperCase() !== 'ALL')
        );
      }
    } else {
      setValue(name, newValue);
    }
  };

  const displayValue = useMemo(() => {
    if (multiple) {
      if (!currentValue || currentValue.length === 0) return [];
      if (currentValue.length === options.length) return optionsWithAll;
      return currentValue;
    }
    return currentValue || null;
  }, [currentValue, multiple, options, optionsWithAll]);

  const selectedCount = useMemo(() => {
    if (!multiple || !currentValue) return 0;
    return Array.isArray(currentValue) ? currentValue.length : 0;
  }, [currentValue, multiple]);

  const displayText = useMemo(() => {
    if (!multiple) return '';
    if (selectedCount === 0) return '';
    if (selectedCount === options.length) return 'All selected';
    return `${selectedCount} selected`;
  }, [selectedCount, multiple, options.length]);

  return (
    <Grid container spacing={1}>
      <Grid item xs>
        <FormControl fullWidth>
          <Autocomplete
            {...register(name)}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: 36,
                minHeight: 36,
                padding: '0 8px',
                alignItems: 'center',
                overflow: 'hidden',
              },
              '& .MuiAutocomplete-input': {
                padding: '0 !important',
                margin: '0 !important',
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={selectedCount === 0 && 'Start typing to search'}
                InputProps={{
                  ...params.InputProps,
                  sx: {
                    height: 36,
                  },
                  startAdornment: multiple && displayText && !isOpen ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                      <Chip
                        label={displayText}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: '12px',
                          backgroundColor: '#e5e7eb',
                          color: '#374151',
                          '& .MuiChip-label': {
                            px: 1,
                          },
                        }}
                      />
                    </Box>
                  ) : params.InputProps.startAdornment,
                }}
              />
            )}
            renderTags={() => null}
            limitTags={0}
            multiple={multiple}
            open={isOpen}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            options={optionsWithAll}
            disableClearable={!multiple}
            disableCloseOnSelect={multiple}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={displayValue}
            onChange={handleChange}
            loading={loading}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option.id}>
                {multiple && (
                  <Checkbox
                    style={{ marginRight: 8 }}
                    checked={
                      option.id === 'ALL'
                        ? currentValue?.length === options.length
                        : selected
                    }
                  />
                )}
                {option.name}
              </li>
            )}
          />
        </FormControl>
      </Grid>
    </Grid>
  );
}
